<template>
	<dropdown-base
    ref="dropdownBaseWrapper"
	dropdownType="account"
	:headerClass="headerHandler === 'balance-header' ? 'medium' : 'big'"
	:params="{ ...params, withSearch: options.length > 9, searchParams: { placeholder: t('inputs.searchPlaceholder') } }"
    :options="options"
	:dropdownIsOpen="dropdownIsOpen"
	:ocultDropArrowIfOnlyOneOption="params && !!params.ocultDropArrowIfOnlyOneOption && options.length === 1"
	@toggleDropdown="dropdownIsOpen = !dropdownIsOpen"
	@closeDropdown="closeDropdown"
    @search="dropdownOptions = $event">
		<!-- Header slot -->
		<template v-slot:header>
			<div class="header" :class="{'active': dropdownIsOpen, 'with-border': headerHandler === 'balance-header' }">
				<!-- Placeholder -->
                <span v-if="params.placeholder && !props.selectedOption" class="placeholder">
                    {{ params.placeholder }}
                </span>
				<!-- Description Header -->
				<div v-if="headerHandler === 'description-header' && props.selectedOption" class="description-header">
					<accounts-icon class="accounts-icon"/>
					<div class="wrapper">
                        <div class="title">{{t('dropdowns.account')}}</div>
                        <div class="account-name">{{selectedAccount.description}}</div>
						<div class="account-number" >
							<span v-mask="ACCOUNT_NUMBER" :key="selectedAccount.id">{{selectedAccount.accountNumber}}</span>
						</div>
					</div>
				</div>
				<!-- Balance Header -->
				<div v-if="headerHandler === 'balance-header' && props.selectedOption" class="balance-header" :key="selectedAccount.id">
					<div
					v-if="selectedAccount.description && selectedAccount.description !== selectedAccount.accountNumber"
					class="account-number"
					:key="selectedAccount.id">
						{{selectedAccount.description}}
					</div>
					<div v-else class="account-number" v-mask="ACCOUNT_NUMBER">{{selectedAccount.accountNumber}}</div>
					<div class="account-balance">
						<balance-positive
						v-if="!hideAmounts && (selectedAccount.availableBalance ? selectedAccount.availableBalance > 0 : selectedAccount.balance > 0)"
						class="positive"/>
						<balance-negative
						v-if="!hideAmounts && (selectedAccount.availableBalance ? selectedAccount.availableBalance < 0 : selectedAccount.balance < 0)"
						class="negative"/>
						{{ amountFormat(Math.abs(selectedAccount.availableBalance || selectedAccount.balance), selectedAccount.currency, null, null, null, hideAmounts) }}
					</div>
				</div>
				<!-- Accounts Groups Header -->
				<div v-if="headerHandler === 'accounts-group' && props.selectedOption" class="description-header">
					<accounts-icon style="flex-shrink: 0"/>
					<div class="wrapper">
						<div class="account-number" :key="selectedAccount.id">{{t('dropdowns.accountsGroupsLabel')}}</div>
						<div class="account-name" :key="selectedAccount.id">{{selectedAccount.id}}</div>
					</div>
				</div>
			</div>
		</template>
		<!-- Body slot -->
		<template v-slot:body>
			<div v-if="dropdownOptions.length" class="dropdown-body">
				<dropdown-row
				v-for="row in dropdownOptions"
				:key="row.id"
				:selectedOption="selectedOption"
				:option="row"
				:hideAmounts="hideAmounts"
				@clicked="[emit('clicked', $event), closeDropdown()]"/>
			</div>
		</template>
	</dropdown-base>
</template>

<script setup>
/* Vue */
import { ref, computed, watch } from 'vue'
/* i18n */
import { useI18n } from 'vue-i18n'
/* Components */
import DropdownBase from '@/components/generic/dropdowns/Base'
import DropdownRow from '@/components/generic/dropdowns/accounts/Row'
import BalancePositive from '@/assets/icons/plus.svg?inline'
import BalanceNegative from '@/assets/icons/minus.svg?inline'
import AccountsIcon from '@/assets/icons/account-balance.svg?inline'
/* Aux */
import { amountFormat } from '@/assets/scripts/amounts'
import { ACCOUNT_NUMBER } from '@/assets/constants/masks'
/* Emmitters */
const emit = defineEmits(['clicked'])

/** props received
@params { Object }
	- labelText { String }
	- mobileTitle {String }
	- mandatory { Boolean }
	- headerType { String } - ['description-header', 'balance-header', 'accounts-group']
	- ocultDropArrowIfOnlyOneOption {Boolean} - option to trigger our drop to hide arrow when only one option is available
    - withSearch { Boolean }
	- searchParams { Object }
@selectedOption { String }
@options { Array } each item is an Object
	- id { String, Number }
	- description { String }
	- accountNumber { String }
	- availableBalance { Number }
	- balance { Number }
	- currency { String }
@hideAmounts {Bollean} prop to hide amounts
*/
const props = defineProps({
	params: Object,
	selectedOption: [String, Number, Boolean],
	options: Array,
    hideAmounts: Boolean
})

const { t } = useI18n()
const dropdownIsOpen = ref(false)
const dropdownOptions = ref(props.options)
const dropdownBaseWrapper = ref('')

const headerHandler = computed(() => {
	return props.params.headerType ? props.params.headerType : 'balance-header'
})

const selectedAccount = computed(() => {
	return props.selectedOption ? props.options.find(option => option.id === props.selectedOption) : ''
})

const closeDropdown = () => {
	dropdownIsOpen.value = false
	dropdownBaseWrapper.value.clearSearchValue()
}

/* Watcher for close dropdown when the selectedOption is empty */
watch(() => props.selectedOption, () => {
    if (props.selectedOption === '') dropdownIsOpen.value = false
})

watch(() => dropdownIsOpen.value, () => {
	dropdownOptions.value = props.options
})
</script>

<style lang="scss" scoped>
.header {
    display: flex;
    align-items: center;

	width: 100%;
	height: 100%;

	padding: 0 34px 0 16px;

    background-color: var(--secondary-light-4);

	cursor: pointer;

	.placeholder {
        @include H6-Regular;
		color: var(--secondary);

        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

	.balance-header {
		width: 100%;
		.account-number {
			@include H6-Regular;
            line-height: 18px;
			color: var(--secondary-dark-3);

            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
		}

		.account-balance {
            display: flex;
			align-items: center;
            gap: 4px;

			@include H8-Regular;
            line-height: 13px;
			color: var(--secondary-dark-1);

			svg {
                flex-shrink: 0;

				width: 16px;
				height: 16px;

				&.positive {
					:deep(.change-color-fill) {
                        fill: var(--positive);
                    }
				}

				&.negative {
					:deep(.change-color-fill) {
                        fill: var(--negative);
                    }
				}
			}
		}
	}

	.description-header {
		width: 100%;
		height: 100%;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;

		display: flex;
		flex-direction: row;
		align-items: center;
        gap: 8px;

		.wrapper {
			width: calc(100% - 18px); // we need this padding to make sure text dont overlap arrow
			height: 100%;

			display: flex;
			flex-direction: column;
			justify-content: center;
			overflow: hidden;

            .title {
                @include H8-Regular;
                line-height: 13px;
                color: var(--secondary-dark-1);

                margin-bottom: 2px;
            }

            .account-name {
				@include H6-Medium;
                line-height: 18px;
                color: var(--secondary-dark-2);

				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}

			.account-number {
				@include H8-Regular;
				color: var(--secondary-dark-1);

				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;

				margin-bottom: 2px;
				text-transform: uppercase;
			}
		}

        svg.accounts-icon {
            flex-shrink: 0;

            width: 24px;
            height: 24px;

            opacity: 0.48;

            :deep(.change-color-fill) {
                fill: var(--primary);
            }
        }
	}

    &.with-border {
        border: 1px solid var(--secondary-light-1);
        border-radius: 8px;

        &.active {
            border: 1px solid var(--primary);
        }

		@media (hover: hover) and (pointer: fine) {
			&:hover {
				border: 1px solid var(--secondary-dark-3);
			}
        }
    }
}
</style>
